/**
 * A condition's value, with the input its property's type calls for: a
 * dropdown of options for a select, yes or no for a boolean, a number box,
 * a date or month picker. Whatever is picked is written as the same plain
 * text the filter has always held (`in` lists comma-separated), so the
 * leads search and the routing engine read it exactly as before.
 */
import type { KeyboardEvent } from 'react';
import {
  Autocomplete, Group, MultiSelect, NumberInput, Select, Text, TextInput, type MantineSize,
} from '@mantine/core';
import type { PropertyDef } from '../../api/properties';
import { formatNumber } from '../../theme';

/** Operators that take no value, a list or a pattern, where one suggested value does not fit. */
const NO_SUGGESTIONS = new Set(['is_empty', 'is_not_empty', 'isempty', 'in', 'not_in', 'regex']);

export function FilterValue({
  def, operator, value, onChange, disabled, placeholder, onKeyDown, size, label, showLabel, style, styles,
  suggestions,
}: {
  def: PropertyDef | null | undefined;
  operator: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  size?: MantineSize;
  label?: string;
  /** Show `label` above the input, as the routing filter rows do. */
  showLabel?: boolean;
  style?: React.CSSProperties;
  /** Mantine styles for the input, as the compact filter rows pass. */
  styles?: { input?: React.CSSProperties };
  /** Values seen on recent records, commonest first, offered as the value is
   *  typed. Anything can still be typed. */
  suggestions?: { value: string; count: number }[];
}) {
  const common = { 'aria-label': label ?? 'Value', label: showLabel ? label : undefined, size, disabled, style, styles };
  const options = (def?.options ?? []).map((o) => ({ value: o.value, label: o.label || o.value }));
  const kind = def?.type;
  if (!disabled && kind) {
    if ((kind === 'select' || kind === 'multi_select') && options.length > 0) {
      if (operator === 'in' || operator === 'not_in') {
        const picked = value.split(',').map((s) => s.trim()).filter(Boolean);
        return (
          <MultiSelect
            {...common}
            searchable
            placeholder={picked.length ? undefined : 'Pick options'}
            data={withCurrent(options, picked)}
            value={picked}
            onChange={(list) => onChange(list.join(','))}
          />
        );
      }
      return (
        <Select
          {...common}
          searchable
          placeholder="Pick an option"
          data={withCurrent(options, value ? [value] : [])}
          value={value || null}
          onChange={(v) => onChange(v ?? '')}
        />
      );
    }
    if (kind === 'boolean') {
      return (
        <Select
          {...common}
          placeholder="Yes or no"
          data={[{ value: 'true', label: 'Yes (true)' }, { value: 'false', label: 'No (false)' }]}
          value={value || null}
          onChange={(v) => onChange(v ?? '')}
        />
      );
    }
    if (kind === 'number' || kind === 'currency') {
      return (
        <NumberInput
          {...common}
          placeholder={kind === 'currency' ? 'Pounds' : 'Number'}
          prefix={kind === 'currency' ? '£' : undefined}
          value={value === '' ? '' : Number(value)}
          onChange={(n) => onChange(n === '' ? '' : String(n))}
          onKeyDown={onKeyDown}
        />
      );
    }
    if ((kind === 'date' || kind === 'datetime') && (operator === 'equals' || operator === 'not_equals')) {
      return <TextInput {...common} type="date" value={value} onChange={(e) => onChange(e.currentTarget.value)} onKeyDown={onKeyDown} />;
    }
    if ((kind === 'date' || kind === 'datetime') && operator === 'starts_with') {
      return <TextInput {...common} type="month" placeholder="YYYY-MM" value={value} onChange={(e) => onChange(e.currentTarget.value)} onKeyDown={onKeyDown} />;
    }
  }
  if (!disabled && suggestions && suggestions.length > 0 && !NO_SUGGESTIONS.has(operator)) {
    const counts = new Map(suggestions.map((s) => [s.value, s.count]));
    return (
      <Autocomplete
        {...common}
        placeholder={placeholder ?? 'Value'}
        data={suggestions.map((s) => s.value)}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        maxDropdownHeight={260}
        comboboxProps={{ width: 280, position: 'bottom-start' }}
        data-testid="filter-value-suggest"
        renderOption={({ option }) => (
          <Group justify="space-between" gap="sm" wrap="nowrap" w="100%">
            <Text size="xs" truncate>{option.value}</Text>
            <Text size="xs" c="dimmed">{formatNumber(counts.get(option.value) ?? 0)}</Text>
          </Group>
        )}
      />
    );
  }
  return (
    <TextInput
      {...common}
      placeholder={placeholder ?? 'Value'}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
      onKeyDown={onKeyDown}
    />
  );
}

/** The options, plus any chosen value that is no longer one of them, so it still shows. */
function withCurrent(options: { value: string; label: string }[], current: string[]) {
  const extra = current.filter((c) => !options.some((o) => o.value === c)).map((c) => ({ value: c, label: c }));
  return [...options, ...extra];
}
